<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form v-model="formData.valid" autocomplete="off" class="mb-3" validate-on="input"
                @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="config">Update WIFI Access Point</h4>
                    <h4 v-else>New WIFI Access Point</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="formData['ssid']" autofocus
                                          :rules="[vRules.required,vRules.maxLength(32)]"
                                          autocomplete="off" density="compact"
                                          hint="comma is not allowed" label="SSID" @input="removeComma"/>

                            <v-text-field v-model="formData['password']"
                                          :rules="[vRules.required,vRules.maxLength(32)]"
                                          autocomplete="off" density="compact" label="Password"
                                          :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                                          :type="show ? 'text' : 'password'"
                                          hint="comma is not allowed"
                                          @input="removeComma"
                                          @click:append="show = !show"
                            />

                            <v-select v-model="formData['access_point']"
                                      :items="types" :rules="[vRules.required]"
                                      density="compact" label="Access point type"/>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" style="height: 350px">
                            <l-map ref="mapContainer" :center="mapCenter" :zoom="15" @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                            </l-map>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import "leaflet-draw";
import L from "leaflet";
import {ref, reactive} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    companyId: Number,
    config: Object,
});
const formData = reactive({
    valid: false,
    lat: props.config?.lat ?? null,
    lon: props.config?.lon ?? null,
    radius: props.config?.radius ?? null,
    ssid: props.config?.ssid ?? null,
    password: props.config?.password ?? null,
    access_point: props.config?.access_point ?? null,
    company_id: props.companyId
});
const types = [
    {value: 0, title: 'Connectivity'},
    {value: 1, title: 'Location Awareness Only'},
    {value: 2, title: 'Both'},
];
const emits = defineEmits(['update:modelValue', 'addConfig', 'updateConfig']);
const dialogSwitch = ref(props.modelValue);
const show = ref(false);
const errors = ref([]);
const mapContainer = ref();
const mapCenter = ref([47.41322, -1.219482]);

const removeComma = (event) => {
    event.target.value = event.target.value.replace(/,/g, '');
}
const mapIsReady = () => {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    /*Add Draw Geo Search Control*/
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider: new OpenStreetMapProvider({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl}),
    });
    map.addControl(geoSearchOptions);

    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: true,
            polygon: false,
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    // Draw circle
    if (props.config) {
        drawnItems.addLayer(L.circle([props.config.lat, props.config.lon], {
            radius: props.config.radius,
        }));
        map.setView([props.config.lat, props.config.lon], 17);
    }

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });

        formData['lat'] = e.layer.getLatLng().lat;
        formData['lon'] = e.layer.getLatLng().lng;
        formData['radius'] = e.layer.getRadius();
        drawnItems.addLayer(e.layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            formData['lat'] = layer.getLatLng().lat;
            formData['lon'] = layer.getLatLng().lng;
            formData['radius'] = layer.getRadius();
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData['lat'] = null;
        formData['lon'] = null;
        formData['radius'] = null;
    });
}

const submitForm = () => {
    errors.value = [];
    if (!formData['lat'] || !formData['lon'] || !formData['radius'] || !formData['ssid'] || !formData['password'] || formData['access_point'] < 0) {
        snackbar.showSnackbar('All fields are required', 'error');
        return;
    }
    send(`${props.config ? 'put' : 'post'}`, `configurations/wifi${props.config ? '/' + props.config.id : ''}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits(props.config ? 'updateConfig' : 'addConfig', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
</script>
